/**
 * AI Coach screen — full conversation history + send composer.
 * Route: /ai-trainer/coach
 *
 * Most recent message at top (reverse-chronological scroll); input bottom-fixed.
 * Daily limit 10 user messages — 11th attempt shows clean toast (server returns 429).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Brain, UserCircle2 } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import BottomNav from "../../components/layout/BottomNav";
import TrainerTaskCard from "../../components/personal-ai-trainer/TrainerTaskCard";

export default function AICoach() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const scrollerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/ai-trainer/coach/messages?limit=60");
      // API returns most-recent first — keep that order so newest is at the top
      setMessages(data || []);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/ai-trainer/coach/messages", { text });
      setRemaining(data?.remaining_today);
      setDraft("");
      // Reload (cheaper than splicing — backend persisted both messages)
      await load();
      // Scroll to top (newest)
      scrollerRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error(detail || "Daily message limit reached.");
      else toast.error(detail || "Could not send");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="app-page min-h-screen pb-32"
      data-testid="ai-coach-screen"
      style={{ background: "#F4F3F1" }}
    >
      <header className="sticky top-0 z-20 bg-canvas/90 backdrop-blur-xl border-b border-line2">
        <div className="max-w-2xl mx-auto h-14 px-3 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            aria-label="Back"
            data-testid="ai-coach-back"
            className="w-10 h-10 grid place-items-center"
          >
            <ChevronLeft size={22} />
          </button>
          <div className="flex items-center gap-2">
            <Brain size={16} style={{ color: "#00B0D5" }} />
            <h1 className="font-barlow-cond font-extrabold text-[22px]">Your AI Trainer</h1>
          </div>
        </div>
      </header>

      <main
        ref={scrollerRef}
        className="max-w-2xl mx-auto px-4 pt-4 pb-32 space-y-3"
        data-testid="ai-coach-thread"
      >
        <TrainerTaskCard />
        {messages === null && <div className="text-center text-ink-soft py-12">Loading…</div>}
        {messages?.length === 0 && (
          <div
            className="bg-white rounded-[14px] p-6 text-center border border-line2"
            data-testid="ai-coach-empty"
          >
            <Brain size={28} style={{ color: "#00B0D5", margin: "0 auto" }} />
            <h3 className="font-barlow-cond font-extrabold text-[20px] mt-2">
              Complete your first workout
            </h3>
            <p className="text-[13px] text-ink-soft mt-1">
              Your AI Trainer will send you personalised feedback after every session
              you finish with the camera on.
            </p>
          </div>
        )}
        {messages?.map((m) => (
          <MessageRow key={m.message_id} message={m} userName={user?.name} navigate={navigate} />
        ))}
      </main>

      {/* Composer */}
      <div
        className="fixed left-0 right-0 z-20"
        style={{ bottom: 64, background: "linear-gradient(to top, rgba(244,243,241,1) 70%, transparent)" }}
      >
        <div className="max-w-2xl mx-auto px-4 py-3 flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 200))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Ask your AI Trainer anything…"
            data-testid="ai-coach-input"
            className="flex-1 h-12 px-4 rounded-pill border border-line bg-white text-[14px] outline-none"
            style={{ borderRadius: 100 }}
          />
          <button
            onClick={send}
            disabled={busy || !draft.trim()}
            data-testid="ai-coach-send"
            aria-label="Send"
            className="w-12 h-12 rounded-full grid place-items-center disabled:opacity-50"
            style={{ background: "#00D4FF", color: "#000" }}
          >
            <Send size={16} />
          </button>
        </div>
        {remaining !== null && (
          <div className="max-w-2xl mx-auto px-4 pb-2 text-[11px] text-ink-tertiary text-right">
            {remaining} message{remaining === 1 ? "" : "s"} remaining today
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}

function MessageRow({ message, userName, navigate }) {
  const isUser = message.role === "user";
  const time = new Date(message.created_at).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`ai-msg-${message.message_id}`}
      data-role={isUser ? "user" : "assistant"}
    >
      <div className="max-w-[88%]">
        <div className={`flex items-center gap-1.5 mb-1 ${isUser ? "justify-end" : "justify-start"}`}>
          {!isUser && <Brain size={12} style={{ color: "#00B0D5" }} />}
          <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-ink-tertiary">
            {isUser ? userName?.split(" ")[0] || "You" : "AI Trainer"}
          </span>
          {!isUser && message.message_type === "post_workout" && (
            <span
              className="text-[9px] font-bold uppercase px-1.5 h-[18px] inline-flex items-center rounded-pill"
              style={{
                background: "rgba(0,212,255,0.14)",
                color: "#00B0D5",
                borderRadius: 100,
              }}
            >
              Post-workout
            </span>
          )}
          <span className="text-[10px] text-ink-tertiary">· {time}</span>
        </div>
        <div
          className="px-3.5 py-2.5 text-[13.5px] leading-snug"
          style={{
            background: isUser ? "#00D4FF" : "white",
            color: isUser ? "#000" : "#1A1A1A",
            borderRadius: 14,
            borderTopRightRadius: isUser ? 4 : 14,
            borderTopLeftRadius: isUser ? 14 : 4,
            border: isUser ? "none" : "1px solid #E2E0DC",
          }}
        >
          {message.text}
        </div>
        {!isUser && message.referral && (
          <button
            onClick={() => navigate("/trainers")}
            data-testid="ai-coach-referral-cta"
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-bold"
            style={{ color: "#00B0D5" }}
          >
            Find a Trainer →
          </button>
        )}
      </div>
    </div>
  );
}
